import { useStaticQuery, graphql } from "gatsby";

interface NavbarRedirection {
  redirectionUrl: string;
  redirectionPath: string;
  redirectionText: string;
  redirectionType: string[];
}

interface SocialRedirection {
  icon: {
    node: {
      publicUrl: string;
    };
  };
  link: string;
  name: string;
}

interface LocationReservation {
  locationReservationToggler: boolean;
  widgetSupplier: string;
  widgetScriptUrl: string;
}

interface LocationPage {
  socialRedirections: SocialRedirection[];
  section02: {
    menuList: {
      redirection: string;
    }[];
  };
}

interface WpLocation {
  title: string;
  slug: string;
  locationReservation: LocationReservation;
  locationPage: LocationPage;
  navbarContent: {
    navbarRedirection: NavbarRedirection[];
  };
}

interface ExternalWebsite {
  websiteRedirection: string;
  websiteName: string;
  websiteImage: {
    node: {
      altText: string;
      title: string;
      publicUrl: string;
    };
  };
}

interface LayoutQueryData {
  wpLayoutElement: {
    layout: {
      externalWebsites: ExternalWebsite[];
      externalWebsitesInternational: ExternalWebsite[];
    };
  };
  allWpLocation: {
    nodes: WpLocation[];
  };
}

interface TransformedLocationData {
  title: string;
  slug: string;
  navbarContent: {
    navbarRedirection: NavbarRedirection[];
  };
  socialRedirections: SocialRedirection[];
  locationReservation: LocationReservation;
  section02: LocationPage["section02"];
}

export const useLayout = () => {
  const data: LayoutQueryData = useStaticQuery(graphql`
    query useLayout {
      wpLayoutElement(slug: { eq: "default" }) {
        layout {
          externalWebsites {
            websiteRedirection
            websiteName
            websiteImage {
              node {
                altText
                title
                publicUrl
              }
            }
          }
          externalWebsitesInternational {
            websiteRedirection
            websiteName
            websiteImage {
              node {
                altText
                title
                publicUrl
              }
            }
          }
        }
      }
      allWpLocation {
        nodes {
          title
          slug
          locationReservation {
            locationReservationToggler
            widgetSupplier
            widgetScriptUrl
            tagScriptUrl
          }
          navbarContent {
            navbarRedirection {
              redirectionUrl
              redirectionPath
              redirectionText
              redirectionType
            }
          }
          locationPage {
            socialRedirections {
              icon {
                node {
                  publicUrl
                }
              }
              link
              name
            }
            section02 {
              menuList {
                redirection
              }
            }
          }
        }
      }
    }
  `);

  const transformLocationData = (
    location: WpLocation
  ): TransformedLocationData => {
    const { title, slug, locationPage, locationReservation, navbarContent } =
      location;

    return {
      title,
      slug,
      navbarContent,
      socialRedirections: locationPage.socialRedirections,
      locationReservation,
      section02: locationPage.section02, 
    };
  };

  const transformedLocations = data.allWpLocation.nodes.map(
    transformLocationData
  );

  const getLocationBySlug = (slug: string) =>
    transformedLocations.find((location) => location.slug === slug);

  return {
    externalWebsites: data.wpLayoutElement?.layout?.externalWebsites || [],
    externalInternational:
      data.wpLayoutElement?.layout?.externalWebsitesInternational || [],
    locations: transformedLocations,
    getLocationBySlug,
  };
};
